import { useState, useMemo, useCallback } from "react";
import { 
  ColumnDef,
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { User } from "@/types/user";
import { DataTable } from "@/components/data-table/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  MoreHorizontal,
  Edit,
  Eye,
  Award,
  Search,
  Trash2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSearchUsers } from "@/hooks/use-users";
import { UserDetailDialog } from "@/components/users/user-detail-dialog";
import { EditUserDialog } from "@/components/users/edit-user-dialog";
import { DeleteUserDialog } from "@/components/users/delete-user-dialog";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/use-debounce";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";

export function UsersPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Debounce search query - increased to 500ms to prevent freezing during fast typing
  const debouncedQuery = useDebounce(searchQuery.trim(), 500);

  // Fetch users based on search (only if query is long enough)
  const { data: searchResults = [], isLoading } = useSearchUsers(debouncedQuery);

  // Convert search results to User type
  const users: User[] = useMemo(() => {
    return searchResults.map((result) => ({
      ...result,
      isAmbassador: false, // This would need to be included in the API response
      name: `${result.firstName} ${result.lastName}`,
    } as User));
  }, [searchResults]);

  const handleViewDetails = useCallback((user: User) => {
    setSelectedUser(user);
    setDetailDialogOpen(true);
  }, []);

  const handleEdit = useCallback((user: User) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  }, []);

  const handleDelete = useCallback((user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  }, []);

  const columns: ColumnDef<User>[] = useMemo(
    () => [
      {
        accessorKey: "id",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="ID" />
        ),
        cell: ({ row }) => (
          <div className="font-medium">{row.getValue("id")}</div>
        ),
        size: 80,
      },
      {
        accessorKey: "name",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Name" />
        ),
        cell: ({ row }) => {
          const firstName = row.original.firstName;
          const lastName = row.original.lastName;
          return (
            <div className="flex items-center gap-2">
              <span className="font-medium text-base">
                {firstName} {lastName}
              </span>
              {row.original.isAmbassador && (
                <Award className="h-4 w-4 text-yellow-500" />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Email" />
        ),
        cell: ({ row }) => (
          <div className="text-sm">
            {row.original.email || <span className="text-muted-foreground">N/A</span>}
          </div>
        ),
      },
      {
        accessorKey: "phoneNumber",
        header: "Phone",
        cell: ({ row }) => (
          <div className="text-sm text-muted-foreground">
            {row.original.phoneNumber || "N/A"}
          </div>
        ),
      },
      {
        accessorKey: "schoolId",
        header: "School",
        cell: ({ row }) =>
          row.original.schoolId ? (
            <Badge variant="secondary" className="font-medium px-3 py-1">
              {row.original.schoolId}
            </Badge>
          ) : (
            <Badge variant="outline" className="font-medium px-3 py-1 text-muted-foreground">
              General
            </Badge>
          ),
      },
      {
        id: "actions",
        cell: ({ row }) => {
          const user = row.original;

          return (
            // React portals bubble events through the React tree, so clicks on
            // the menu items would otherwise also hit the row's onRowClick
            // handler and open the detail dialog on top of the chosen action.
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleViewDetails(user)}>
                    <Eye className="mr-2 h-4 w-4" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleEdit(user)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => handleDelete(user)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [handleViewDetails, handleEdit, handleDelete]
  );

  const shouldShowResults = debouncedQuery.length >= 4;

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    enableRowSelection: false,
  });

  return (
    <div className="container mx-auto space-y-8 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Users</h1>
        <p className="text-muted-foreground mt-1">
          Search and manage users across all schools
        </p>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-2xl">
        <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search users by name, email, phone, or ID (min 4 characters)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12 text-base"
        />
      </div>

      {/* Results Table - Only show when there's a search query */}
      {searchQuery && (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            {searchQuery.trim().length < 4 ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-sm text-muted-foreground">
                  Please enter at least 4 characters to search
                </p>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">Loading users...</p>
              </div>
            ) : users.length > 0 ? (
              <DataTable
                table={table}
                columns={columns}
                onRowClick={handleViewDetails}
              />
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-muted-foreground">No users found</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Try a different search term
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <UserDetailDialog
        user={selectedUser}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
      />

      <EditUserDialog
        user={selectedUser}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={() => {
          setEditDialogOpen(false);
          setSelectedUser(null);
        }}
      />

      <DeleteUserDialog
        user={selectedUser}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={() => {
          setSelectedUser(null);
        }}
      />
    </div>
  );
}